import { t } from '@repo/i18n';
import { getLocale } from '@repo/i18n/browser';
import { useLocale } from '@repo/i18n/react';
import { Avatar, AvatarFallback } from '@repo/ui/avatar';
import { DataTable } from '@repo/ui/data-table';
import { formatDate } from '@repo/ui/lib/formatters';
import { Skeleton } from '@repo/ui/skeleton';
import { TableCell } from '@repo/ui/table';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import type { RouterOutputs } from '@/providers/trpc-client';
import type { TTableFilters } from '@/types/table';
import type { TOrganizationsTableFilters } from './organizations-table-filters';
import { OwnersTableActions } from './owners-table-actions';
import { OwnersTableEmptyState } from './owners-table-empty-state';
import { OwnersTableErrorState } from './owners-table-error-state';
import { OwnersTableFilters } from './owners-table-filters';

type OwnerTableDataType = RouterOutputs['admin']['listOwnerUsers']['data'];

function getOwnerInitials(owner: OwnerTableDataType[number]['user']): string {
  if (owner.firstName || owner.lastName) {
    const first = owner.firstName?.[0] ?? '';
    const last = owner.lastName?.[0] ?? '';
    return `${first}${last}`.toUpperCase() || '?';
  }
  if (owner.email) {
    return owner.email.substring(0, 2).toUpperCase();
  }
  return '?';
}

function getOwnerDisplayName(owner: OwnerTableDataType[number]['user']): string {
  const parts = [owner.firstName, owner.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : (owner.email ?? 'N/A');
}

interface OwnersTableProps extends Omit<TTableFilters<TOrganizationsTableFilters>, 'hasFilters'> {
  data: OwnerTableDataType;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  onRefetch: () => void;
}

export function OwnersTable({
  data,
  isLoading,
  isError,
  error,
  onRefetch,
  filters,
  onFilterChange,
  onClearFilters,
}: OwnersTableProps) {
  useLocale();

  const columns = useMemo<ColumnDef<OwnerTableDataType[number]>[]>(
    () => [
      {
        get header() {
          return t('workspace.ownerName');
        },
        cell: ({ row }) => {
          const owner = row.original.user;
          return (
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="text-xs">{getOwnerInitials(owner)}</AvatarFallback>
              </Avatar>
              <span className="font-medium text-sm">{getOwnerDisplayName(owner)}</span>
            </div>
          );
        },
      },
      {
        get header() {
          return t('reports.email');
        },
        cell: ({ row }) => (
          <div className="text-muted-foreground text-sm">{row.original?.user?.email || 'N/A'}</div>
        ),
      },
      {
        get header() {
          return t('workspace.organization');
        },
        cell: ({ row }) => {
          const orgName = row.original.organization;
          return (
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="text-xs">
                  {orgName ? orgName?.name?.substring(0, 2).toUpperCase() : '?'}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm">{orgName?.name || 'N/A'}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'joinedAt',
        get header() {
          return t('workspace.createdAt');
        },
        cell: ({ row }) => (
          <div className="whitespace-nowrap text-muted-foreground text-sm">
            {row.original.joinedAt ? formatDate(row.original.joinedAt) : 'N/A'}
          </div>
        ),
      },
      {
        get header() {
          return t('workspace.accountStatus');
        },
        cell: ({ row }) => <OwnersTableActions owner={row.original.user} />,
      },
    ],
    [getLocale()],
  );

  return (
    <div className="space-y-4">
      <OwnersTableFilters
        filters={filters}
        hasFilters={!!filters.search}
        onClearFilters={onClearFilters}
        onFilterChange={onFilterChange}
      />
      <DataTable
        columns={columns}
        data={data}
        emptyState={
          <OwnersTableEmptyState
            hasFilters={!!filters.search}
            onClearFilters={onClearFilters}
            searchQuery={filters.search}
          />
        }
        error={{
          enable: isError || false,
          component: (
            <TableCell className="h-32 text-center" colSpan={columns.length}>
              <OwnersTableErrorState error={error} onRefetch={onRefetch} />
            </TableCell>
          ),
        }}
        skeleton={{
          enable: isLoading,
          rows: 5,
          component: columns.map((_, colIndex) => (
            <TableCell key={`skeleton-col-${colIndex}`}>
              <Skeleton className="h-8 w-full" />
            </TableCell>
          )),
        }}
      />
    </div>
  );
}
